import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import accountsApi from '../../services/accountsApi';
import toast from 'react-hot-toast';

const todayISO = () => new Date().toISOString().slice(0, 10);

const TrialBalance = () => {
  const navigate = useNavigate();
  const [funds, setFunds] = useState([]);
  const [costCenters, setCostCenters] = useState([]);

  const [dateMode, setDateMode] = useState('as_of'); // 'as_of' | 'range'
  const [asOfDate, setAsOfDate] = useState(todayISO());
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [fundId, setFundId] = useState('');
  const [costCenterId, setCostCenterId] = useState('');

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const loadRefs = async () => {
      try {
        const [fundsRes, ccRes] = await Promise.all([
          accountsApi.get('/funds'),
          accountsApi.get('/cost-centers', { params: { is_active: true } }),
        ]);
        setFunds(fundsRes.data.funds);
        setCostCenters(ccRes.data.cost_centers);
      } catch (err) {
        toast.error('Failed to load filter options');
      }
    };
    loadRefs();
  }, []);

  const runReport = async () => {
    try {
      setLoading(true);
      const params = {};
      if (dateMode === 'as_of') {
        if (asOfDate) params.as_of_date = asOfDate;
      } else {
        if (fromDate) params.from_date = fromDate;
        if (toDate) params.to_date = toDate;
      }
      if (fundId) params.fund_id = fundId;
      if (costCenterId) params.cost_center_id = costCenterId;
      const response = await accountsApi.get('/trial-balance', { params });
      setData(response.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load trial balance');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { runReport(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Layout title="Trial Balance">
      <div className="bg-white rounded-xl shadow p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Date Mode</label>
            <select value={dateMode} onChange={(e) => setDateMode(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="as_of">As Of Date</option>
              <option value="range">Date Range</option>
            </select>
          </div>
          {dateMode === 'as_of' ? (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">As Of</label>
              <input type="date" value={asOfDate} onChange={(e) => setAsOfDate(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          ) : (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">From</label>
                <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">To</label>
                <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Fund</label>
            <select value={fundId} onChange={(e) => setFundId(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">All Funds</option>
              {funds.map(f => <option key={f.id} value={f.id}>{f.code} — {f.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Cost Center</label>
            <select value={costCenterId} onChange={(e) => setCostCenterId(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">All Cost Centers</option>
              {costCenters.map(cc => <option key={cc.id} value={cc.id}>{cc.code} — {cc.name}</option>)}
            </select>
          </div>
          <button onClick={runReport} className="bg-blue-800 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-900">
            Apply
          </button>
        </div>
      </div>

      {data?.note && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800 mb-4">
          {data.note}
        </div>
      )}

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Code</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Name</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Type</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Normal Balance</th>
              <th className="text-right px-4 py-3 text-gray-600 font-semibold">Debit</th>
              <th className="text-right px-4 py-3 text-gray-600 font-semibold">Credit</th>
              <th className="text-right px-4 py-3 text-gray-600 font-semibold">Balance</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-8 text-gray-500">Loading...</td></tr>
            ) : !data || data.trial_balance.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-8 text-gray-500">No accounts found</td></tr>
            ) : data.trial_balance.map(a => (
              <tr key={a.id}
                onClick={() => navigate(`/ledgers?account_id=${a.id}&to_date=${dateMode === 'as_of' ? asOfDate : (toDate || todayISO())}${dateMode === 'range' && fromDate ? `&from_date=${fromDate}` : ''}`)}
                className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer">
                <td className="px-4 py-2 font-mono text-gray-700">{a.account_code}</td>
                <td className="px-4 py-2 font-medium text-gray-800">{a.account_name}</td>
                <td className="px-4 py-2 text-gray-600">{a.account_type}</td>
                <td className="px-4 py-2 text-gray-600">{a.normal_balance || '—'}</td>
                <td className="px-4 py-2 text-right text-gray-800">{Number(a.total_debit) > 0 ? Number(a.total_debit).toFixed(2) : ''}</td>
                <td className="px-4 py-2 text-right text-gray-800">{Number(a.total_credit) > 0 ? Number(a.total_credit).toFixed(2) : ''}</td>
                <td className="px-4 py-2 text-right font-medium text-gray-800">{Number(a.balance).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
          {data && data.trial_balance.length > 0 && (
            <tfoot>
              <tr className="border-t border-gray-200 font-semibold bg-gray-50">
                <td colSpan={4} className="px-4 py-3 text-right text-gray-600 text-xs">Total</td>
                <td className="px-4 py-3 text-right text-gray-800">{Number(data.total_debit).toFixed(2)}</td>
                <td className="px-4 py-3 text-right text-gray-800">{Number(data.total_credit).toFixed(2)}</td>
                <td className={`px-4 py-3 text-right ${data.total_debit === data.total_credit ? 'text-green-700' : 'text-red-600'}`}>
                  {data.total_debit === data.total_credit ? '✓ Balanced' : '✗ Not balanced'}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </Layout>
  );
};

export default TrialBalance;
